import { useEffect, useState } from 'react';
import { useFocusOnChange } from '../ui/shell';
import { MAX_PLAYERS, NAME_MAX, normalizeCode, sanitizeText } from '@shared/protocol';
import { plural } from '@shared/scoring';
import { DECKS, type DeckId } from '@shared/content';

export type HomeChoice =
  | { kind: 'local'; names: string[]; passes: 1 | 2 }
  | { kind: 'solo' }
  | { kind: 'host' }
  | { kind: 'join'; code: string };

/** The front door. Three ways in, and solo is reached by choosing 1 player. */
export function Home({
  onChoose,
  deck,
  onDeckChange,
}: {
  onChoose: (choice: HomeChoice) => void;
  deck: DeckId;
  onDeckChange: (deck: DeckId) => void;
}) {
  const [pane, setPane] = useState<'home' | 'onephone' | 'join'>('home');
  const headingRef = useFocusOnChange<HTMLHeadingElement>(pane);

  if (pane === 'onephone')
    return <OnePhoneSetup deck={deck} onBack={() => setPane('home')} onStart={onChoose} />;
  if (pane === 'join') return <JoinPane onBack={() => setPane('home')} onJoin={(code) => onChoose({ kind: 'join', code })} />;

  return (
    <section className="screen">
      <div className="bar">
        <span className="eyebrow">A party game</span>
        <span className="eyebrow">1–6 players</span>
      </div>
      <div className="body-area">
        <div>
          <h1 className="wordmark" tabIndex={-1} ref={headingRef}>
            NEEDLE
          </h1>
          <p className="tagline">
            One person sees a hidden spot on a spectrum and writes a clue that lands on it. Everyone else guesses
            where. Points for getting close.
          </p>
        </div>
        <div className="field">
          <span className="eyebrow" id="lbl-mode">
            Select a mode
          </span>
          <div className="seg wide" role="group" aria-labelledby="lbl-mode">
            {(Object.keys(DECKS) as DeckId[]).map((id) => (
              <button
                key={id}
                type="button"
                aria-pressed={deck === id}
                onClick={() => onDeckChange(id)}
              >
                {DECKS[id].label}
              </button>
            ))}
          </div>
          <p className="note">{DECKS[deck].blurb}</p>
        </div>

        <details>
          <summary>How it works</summary>
          <div className="rules">
            <p>
              Each round one player is the <b>clue-giver</b>. They alone see the target on a 0–10 dial, then write one
              short clue that sits at that exact spot.
            </p>
            <p>
              Everyone else drags the needle to where they think the clue lands. <b>4 points</b> for a bullseye,{' '}
              <b>3</b> for close, <b>2</b> for nearly. The clue-giver earns the average of everyone&rsquo;s points — so
              a vague clue costs you.
            </p>
          </div>
        </details>
      </div>
      <div className="actions">
        <button type="button" className="btn" onClick={() => setPane('onephone')}>
          Play on one phone
        </button>
        <button type="button" className="btn ghost" onClick={() => onChoose({ kind: 'host' })}>
          Start a remote game
        </button>
        <button type="button" className="btn ghost" onClick={() => setPane('join')}>
          Join with a code
        </button>
      </div>
    </section>
  );
}

function OnePhoneSetup({
  deck,
  onBack,
  onStart,
}: {
  deck: DeckId;
  onBack: () => void;
  onStart: (choice: HomeChoice) => void;
}) {
  const [count, setCount] = useState(4);
  const [passes, setPasses] = useState<1 | 2>(1);
  const [names, setNames] = useState<string[]>(['', '', '', '', '', '']);
  const headingRef = useFocusOnChange<HTMLHeadingElement>('onephone');

  const solo = count === 1;
  const filled = names.slice(0, count).map((n, i) => sanitizeText(n).slice(0, NAME_MAX) || `Player ${i + 1}`);
  const rounds = count * passes;

  return (
    <section className="screen">
      <div className="bar">
        <button type="button" className="eyebrow linkish" onClick={onBack}>
          Back
        </button>
        <span className="eyebrow">{DECKS[deck].label} mode</span>
      </div>
      <div className="body-area top">
        <h1 className="wordmark" tabIndex={-1} ref={headingRef}>
          NEEDLE
        </h1>

        <div className="field">
          <span className="eyebrow" id="lbl-players">
            Players
          </span>
          <div className="seg" role="group" aria-labelledby="lbl-players">
            {[1, 2, 3, 4, 5, 6].map((n) => (
              <button
                key={n}
                type="button"
                aria-pressed={count === n}
                onClick={() => setCount(n)}
                aria-label={n === 1 ? 'One player, solo' : `${n} players`}
              >
                {n}
              </button>
            ))}
          </div>
          {solo && (
            <p className="note">
              Solo: ten clues someone else wrote. Guess where they meant.
              {deck !== 'normal' && ' Solo always uses the normal set.'}
            </p>
          )}
        </div>

        {!solo && (
          <>
            <div className="field">
              <span className="eyebrow">Names</span>
              <div className="names">
                {Array.from({ length: count }, (_, i) => (
                  <input
                    key={i}
                    type="text"
                    value={names[i]}
                    maxLength={NAME_MAX}
                    autoCapitalize="words"
                    autoCorrect="off"
                    spellCheck={false}
                    placeholder={`Player ${i + 1}`}
                    aria-label={`Player ${i + 1} name`}
                    onChange={(e) => {
                      const next = names.slice();
                      next[i] = e.target.value;
                      setNames(next);
                    }}
                  />
                ))}
              </div>
            </div>

            <div className="field">
              <span className="eyebrow" id="lbl-length">
                Game length
              </span>
              <div className="seg wide" role="group" aria-labelledby="lbl-length">
                <button type="button" aria-pressed={passes === 1} onClick={() => setPasses(1)}>
                  One pass
                </button>
                <button type="button" aria-pressed={passes === 2} onClick={() => setPasses(2)}>
                  Two passes
                </button>
              </div>
              <p className="note">
                {count === 2
                  ? `${plural(rounds, 'round')}. With two players you always score the same — it plays as co-op, so push the total as high as you can.`
                  : `Everyone gives ${passes === 1 ? 'one clue' : 'two clues'} — ${plural(rounds, 'round')}.`}
              </p>
            </div>
          </>
        )}
      </div>
      <div className="actions">
        <button
          type="button"
          className="btn"
          onClick={() => (solo ? onStart({ kind: 'solo' }) : onStart({ kind: 'local', names: filled, passes }))}
        >
          {solo ? 'Start solo' : 'Start game'}
        </button>
      </div>
    </section>
  );
}

function JoinPane({ onBack, onJoin }: { onBack: () => void; onJoin: (code: string) => void }) {
  const [code, setCode] = useState('');
  const headingRef = useFocusOnChange<HTMLHeadingElement>('join');
  const ready = code.length === 4;

  useEffect(() => {
    document.getElementById('join-code')?.focus();
  }, []);

  return (
    <section className="screen">
      <div className="bar">
        <button type="button" className="eyebrow linkish" onClick={onBack}>
          Back
        </button>
        <span className="eyebrow">Join a game</span>
      </div>
      <form
        className="body-area"
        onSubmit={(e) => {
          e.preventDefault();
          if (ready) onJoin(code);
        }}
      >
        <h1 className="waiting-h" tabIndex={-1} ref={headingRef}>
          Type the code
        </h1>
        <p className="waiting-sub">Four characters, from whoever started the game.</p>
        <label className="visually-hidden" htmlFor="join-code">
          Room code
        </label>
        <input
          id="join-code"
          type="text"
          className="code"
          value={code}
          inputMode="text"
          autoCapitalize="characters"
          autoCorrect="off"
          spellCheck={false}
          enterKeyHint="go"
          maxLength={4}
          placeholder="····"
          onChange={(e) => setCode(normalizeCode(e.target.value).slice(0, 4))}
        />
        <button type="submit" className="visually-hidden">
          Join
        </button>
      </form>
      <div className="actions">
        <button type="button" className="btn" disabled={!ready} onClick={() => onJoin(code)}>
          Join room
        </button>
      </div>
      <p className="note center">Up to {MAX_PLAYERS} players.</p>
    </section>
  );
}
